import { type ReactNode, useState } from "react";
import { GroupChart } from "@/charts/GroupChart";
import { ChartBox, ChartInfo } from "@/components/ChartInfo";
import { type HeadCard, Headline } from "@/components/Headline";
import { formatMonth, formatMonthShort, MONTHS_SHORT, type PDate } from "@/plan-logic/lib/dates";
import { money, pct, rate } from "@/plan-logic/lib/format";
import {
  debtClear,
  debtComparison,
  type DebtComparison,
  type DebtSummary,
  debtSummaries,
  debtYearRows,
  hasShortfall,
  interestComparedUntil,
  KIND_LABEL,
} from "@/plan-logic/plan-view";
import { PlanPage, type PlanCtx } from "../PlanPage";

function Ring({ value, color }: { value: number; color: string }) {
  const R = 34;
  const C = 2 * Math.PI * R;
  const v = Math.max(0, Math.min(1, value));
  return (
    <svg width="94" height="94" viewBox="0 0 94 94" aria-hidden="true">
      <circle cx="47" cy="47" r={R} fill="none" stroke="var(--line)" strokeWidth="11" />
      <circle
        cx="47"
        cy="47"
        r={R}
        fill="none"
        stroke={color}
        strokeWidth="11"
        strokeLinecap="round"
        strokeDasharray={`${(C * v).toFixed(1)} ${C.toFixed(1)}`}
        transform="rotate(-90 47 47)"
      />
    </svg>
  );
}

const yearLabel = (v: number) =>
  `${MONTHS_SHORT[Math.max(0, Math.min(11, Math.round((v % 1) * 12)))]} ${Math.floor(v)}`;

const months = (c: DebtComparison) =>
  c.monthsToDebtFree !== undefined ? `${c.monthsToDebtFree} months` : "Not in the plan";

function Column({
  title,
  c,
  color,
  plan,
  until,
}: {
  title: string;
  c: DebtComparison;
  color: string;
  plan?: boolean;
  /** Interest is compared only to here (a plan runs short first). */
  until?: PDate;
}) {
  return (
    <div className={`dcol${plan ? " plan" : ""}`}>
      <div className="cap">{title}</div>
      <Ring value={c.shareOfIncome ?? 0} color={color} />
      <div className="rv2">{pct(c.shareOfIncome)}</div>
      <div className="rl">of your income goes to debt</div>
      <div className="kv">
        <span>Every month</span>
        <b>{money(c.monthlyPayment)}</b>
      </div>
      <div className="kv">
        <span>{until ? `Interest to ${formatMonthShort(until)}` : "Lifetime interest"}</span>
        <b>{money(c.lifetimeInterest)}</b>
      </div>
      <div className="kv">
        <span>Debt-free in</span>
        <b>{months(c)}</b>
      </div>
    </div>
  );
}

/** "a, b and c". */
const andList = (xs: string[]) =>
  xs.length < 2 ? (xs[0] ?? "") : `${xs.slice(0, -1).join(", ")} and ${xs.at(-1)}`;

/**
 * The debt headline (v45 `headDebt`), in one of two shapes from the engine's two runs: when Abi's
 * plan clears the debt sooner, the date and the interest; when it deliberately pays debt down
 * slower, what goes to debt each month, today's path against the plan.
 */
export function debtHeadCards(
  sums: DebtSummary[],
  a: DebtComparison,
  n: DebtComparison | undefined,
  rates: string[],
  opts: {
    /** Interest is compared only to here: the earlier shortfall (`interestComparedUntil`). */
    until?: PDate;
    /** The plan's start, when the plan is out of date: "N months away" counts from it. */
    staleFrom?: PDate;
  } = {},
): HeadCard[] {
  const away = (m: number) =>
    opts.staleFrom ? `${m} months after ${formatMonth(opts.staleFrom)}` : `${m} months away`;
  const toUntil = opts.until ? ` to ${formatMonth(opts.until)}` : "";
  const first: HeadCard = {
    k: "What you owe",
    v: money(a.totalOwed),
    tone: "debt",
    s: `across ${sums.length} debt${sums.length === 1 ? "" : "s"}${rates.length ? `, at ${andList(rates)}` : ""}`,
    testId: "debt-total",
  };
  const when = (c: DebtComparison) => (c.debtFree ? formatMonthShort(c.debtFree) : undefined);
  if (!n) {
    return [
      first,
      {
        k: "With Abi’s plan",
        abi: true,
        v: when(a) ? `Debt free ${when(a)}` : "Not debt free in this plan",
        s: when(a) ? away(a.monthsToDebtFree!) : undefined,
      },
    ];
  }
  const mNow = n.monthsToDebtFree;
  const mPlan = a.monthsToDebtFree;
  const sooner = mPlan !== undefined && (mNow === undefined || mPlan < mNow);
  const dInt = n.lifetimeInterest - a.lifetimeInterest;
  if (sooner) {
    return [
      first,
      {
        k: "On your current plan",
        v: mNow !== undefined ? `Debt free ${when(n)}` : "Never debt free",
        s:
          mNow !== undefined
            ? `${away(mNow)}, and ${money(n.lifetimeInterest)} of interest ${opts.until ? `to ${formatMonth(opts.until)}` : "between here and there"}`
            : "on the path you're on, what you owe never reaches zero",
      },
      {
        k: "With Abi’s plan",
        abi: true,
        from: when(n) ?? "Never",
        to: `${when(a)} debt free`,
        lead: mNow !== undefined ? `${mNow - mPlan!} months sooner` : "Debt free",
        s:
          Math.round(dInt) > 0
            ? `and ${money(dInt)} less interest${toUntil}`
            : Math.round(dInt) < 0
              ? `for ${money(-dInt)} more interest${toUntil}`
              : `for the same interest${toUntil}`,
        testId: "hd-abi",
      },
    ];
  }
  const d = n.monthlyPayment - a.monthlyPayment;
  return [
    first,
    {
      k: "On your current plan",
      v: `${money(n.monthlyPayment)} a month`,
      s: "of what you earn going to your lenders",
    },
    {
      k: "With Abi’s plan",
      abi: true,
      from: money(n.monthlyPayment),
      to: `${money(a.monthlyPayment)} to debt/month`,
      lead: `${d >= 0 ? "+" : "−"}${money(Math.abs(d))} spare/month`,
      leadTone: Math.round(d) > 0 ? "ok" : Math.round(d) < 0 ? "debt" : "flat",
      s:
        Math.round(d) > 0
          ? "for your priorities instead, because the interest is cheap enough that it should go there"
          : "while the plan works through what you owe",
      testId: "hd-abi",
    },
  ];
}

/** One debt at a time, two bars a year: still owing on today's path and on the plan (v44). */
function DebtCurve({ ctx, sums }: { ctx: PlanCtx; sums: DebtSummary[] }) {
  const { abi, naive, index } = ctx;
  const [view, setView] = useState(0);
  const d = sums[Math.min(view, sums.length - 1)];
  const name = (s: DebtSummary) => index.name(s.uuid, KIND_LABEL[s.kind]);
  const rows = debtYearRows(abi, naive, d.uuid);
  const now = naive ? debtClear(naive, d.uuid) : undefined;
  const plan = debtClear(abi, d.uuid);
  const start = ctx.planStart;
  const inMonths = (c: ReturnType<typeof debtClear>) =>
    c.date ? Math.max(0, (c.date.year - start.year) * 12 + c.date.month - start.month) : undefined;
  const r = index.get(d.uuid)?.rate;
  const pm = inMonths(plan);
  const nm = now ? inMonths(now) : undefined;
  const planText =
    pm !== undefined ? `Abi's plan clears it in ${pm} months` : "Abi's plan doesn't clear it";
  const note = now?.never
    ? `On today's path the balance never comes down to zero. ${planText}.`
    : `${r !== undefined ? `${rate(r)} on ` : ""}${money(d.balance)} today. ${planText}${
        nm !== undefined ? ` against ${nm} on today's path` : ""
      }.`;
  return (
    <div className="card pad" style={{ marginBottom: 16 }}>
      <div className="cf-bar" style={{ marginBottom: 14 }}>
        <div>
          <h2 style={{ marginBottom: 4 }}>Your debts, year by year</h2>
          <p className="mut" style={{ margin: 0, maxWidth: "62ch" }}>
            What is still owing at the end of each year
            {naive ? (
              <>, on today&rsquo;s path in blue and on Abi&rsquo;s plan in gold</>
            ) : (
              <> on Abi&rsquo;s plan</>
            )}
            . One debt at a time, because a mortgage and a card cannot share a scale.
          </p>
        </div>
        {sums.length > 1 && (
          <div
            className="cfvtabs"
            id="dbtabs"
            style={{ margin: 0 }}
            role="tablist"
            aria-label="Debts"
          >
            {sums.map((s, i) => (
              <button
                type="button"
                key={s.uuid}
                role="tab"
                aria-selected={i === view}
                className={`cfvtab dbtab${i === view ? " on" : ""}`}
                onClick={() => setView(i)}
                data-testid={`debt-tab-${i}`}
              >
                {name(s)}
              </button>
            ))}
          </div>
        )}
      </div>
      <ChartBox id="debtcurve">
        <GroupChart
          rows={rows.map((x) => ({
            label: x.label,
            // a plan with no state that year (it stopped short) draws no bar, rather than a zero
            values: {
              ...(naive && x.now !== undefined ? { now: x.now } : {}),
              ...(x.plan !== undefined ? { plan: x.plan } : {}),
            },
          }))}
          series={[
            ...(naive ? [{ k: "now", n: "Today’s path", c: "var(--before)" }] : []),
            { k: "plan", n: "Abi’s plan", c: "var(--after)" },
          ]}
          height={300}
          label={`${name(d)} balance each year to ${rows.at(-1)?.year ?? ""}${
            naive ? ", today's path against Abi's plan" : ", on Abi's plan"
          }`}
          note={note}
          testId="debt-curve"
        />
      </ChartBox>
    </div>
  );
}

function Body({ ctx }: { ctx: PlanCtx }) {
  const { abi, naive, index } = ctx;
  const sums = debtSummaries(abi);
  if (!sums.length) {
    return (
      <div className="card pad" data-testid="no-debt">
        <h2 style={{ marginBottom: 6 }}>You don&rsquo;t owe anything</h2>
        <p className="mut">
          No mortgage, cards or loans in your plan. If that&rsquo;s wrong, add them on Update Data.
        </p>
      </div>
    );
  }
  const name = (d: DebtSummary) => index.name(d.uuid, KIND_LABEL[d.kind]);
  // The engine stops a plan at its first shortfall, so interest is compared only up to the earlier
  // one (as Decisions compares a what-if), and said so, never "over the life of the debt".
  const until = interestComparedUntil(abi, naive);
  const a = debtComparison(abi, until);
  const n = naive ? debtComparison(naive, until) : undefined;
  const whoShort =
    hasShortfall(abi) && hasShortfall(naive)
      ? "both run short"
      : hasShortfall(abi)
        ? "Abi’s plan runs short"
        : "today’s path runs short";
  const lifeOf = until ? `to ${formatMonth(until)}, when ${whoShort}` : "over the life of the debt";
  const dMonths =
    n && a.monthsToDebtFree !== undefined && n.monthsToDebtFree !== undefined
      ? a.monthsToDebtFree - n.monthsToDebtFree
      : undefined;
  const dInt = n ? a.lifetimeInterest - n.lifetimeInterest : undefined;
  const rated = sums
    .map((d) => ({ d, r: index.get(d.uuid)?.rate }))
    .filter((x): x is { d: DebtSummary; r: number } => x.r !== undefined);
  const clears = sums.map((d) => ({
    d,
    now: naive ? debtClear(naive, d.uuid) : undefined,
    plan: debtClear(abi, d.uuid),
  }));
  const lo = ctx.planStart.year;
  const hi =
    Math.ceil(Math.max(lo + 1, ...clears.map((c) => Math.max(c.now?.at ?? 0, c.plan.at ?? 0)))) + 1;
  const X = (v: number) => Math.max(2, Math.min(100, ((v - lo) / (hi - lo)) * 100));

  // Where the plan looks worse, say why, under the comparison that shows it (v44): one
  // explanation per measure, each only when that measure is actually worse under the plan.
  const dearest = [...rated].sort((x, y) => y.r - x.r)[0];
  const cheapest = [...rated].sort((x, y) => x.r - y.r)[0];
  const why: [string, ReactNode][] = [];
  if (n && (a.shareOfIncome ?? 0) > (n.shareOfIncome ?? 0) + 0.005)
    why.push([
      "putting more of your income at debt",
      <>
        Abi is sending <b>{pct(a.shareOfIncome)}</b> of what you earn at your debt instead of{" "}
        {pct(n.shareOfIncome)}.
        {dearest &&
          ` Every month the ${name(dearest.d)} sits there costs ${rate(dearest.r)}, which is more than that money can earn anywhere else, so clearing it first is worth the squeeze.`}{" "}
        The extra stops the month the balance does.
      </>,
    ]);
  if (dMonths !== undefined && dMonths > 0)
    why.push([
      "paying your debt off later",
      <>
        The last debt clears <b>{dMonths} months later</b> than on today&rsquo;s path. The plan is
        not trying to be debt-free soonest; it is trying to get the most of your priorities paid for
        {cheapest
          ? `, and the ${name(cheapest.d)} at ${rate(cheapest.r)} is cheap enough that the money does better elsewhere.`
          : "."}
      </>,
    ]);
  if (dInt !== undefined && dInt >= 1)
    why.push([
      "paying more interest",
      <>
        You pay <b>{money(dInt)} more interest</b> {lifeOf}. That is what carrying cheap debt for
        longer costs, and Abi took the trade because the money it freed up is worth more against
        your priorities than the interest it saves.
      </>,
    ]);

  return (
    <>
      <Headline
        id="hd-debt"
        cards={debtHeadCards(
          sums,
          a,
          n,
          rated.map((x) => rate(x.r)),
          { until, staleFrom: ctx.stale ? ctx.planStart : undefined },
        )}
      />

      {n && (
        <div className="card pad dcmp" style={{ marginBottom: 16 }} data-testid="debt-compare">
          <div className="dcmp-head">
            <div>
              <h2 style={{ marginBottom: 4 }}>
                Today&rsquo;s path against the plan
                <ChartInfo k="dcmp-rings" inline />
              </h2>
              <p className="mut" style={{ margin: 0, maxWidth: "62ch" }}>
                The plan decides which debt to push hardest at, and when to leave cheap debt alone.
              </p>
            </div>
            <div className="dcmp-impact" id="dcmp-impact">
              {dMonths !== undefined && dMonths !== 0 && (
                <div className="dchip">
                  <div className="v">
                    {Math.abs(dMonths)} months {dMonths > 0 ? "longer" : "sooner"}
                  </div>
                  <div className="l">until everything you owe is cleared</div>
                </div>
              )}
              {dInt !== undefined && Math.abs(dInt) >= 1 && (
                <div className="dchip">
                  <div className="v">
                    {money(Math.abs(dInt))} {dInt > 0 ? "more" : "less"} interest
                  </div>
                  <div className="l" data-testid="debt-interest-span">
                    {lifeOf}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div id="dcmp-rings">
            <div className="dcmp-pair">
              <Column title="Today’s path" c={n} color="var(--before)" until={until} />
              <div className="dcmp-arrow">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M4 12 H19 M13 6 L19 12 L13 18"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <Column title="Abi’s plan" c={a} color="var(--after)" plan until={until} />
            </div>
          </div>
          <div id="dcmp-why" data-testid="debt-why">
            {why.length > 0 && (
              <div className="dwhy">
                <h3>Why Abi might recommend {andList(why.map((w) => w[0]))}</h3>
                {why.map(([k, p]) => (
                  <p key={k}>{p}</p>
                ))}
                <p className="dwf">
                  If you would rather it did none of this, raise where paying off debt sits on your
                  priorities and the plan will change.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      <DebtCurve ctx={ctx} sums={sums} />

      <div className="card pad" style={{ marginBottom: 16 }}>
        <h2 style={{ marginBottom: 4 }}>
          What each payment actually does
          <ChartInfo k="debtsplit" inline />
        </h2>
        <p className="mut" style={{ marginBottom: 16 }}>
          Part of every payment clears the balance. The rest is rent on money you already spent.
        </p>
        <div id="debtsplit" data-testid="debt-split">
          {sums.map((d) => {
            const interest = Math.min(d.monthlyInterest, d.monthlyPayment);
            const prin = Math.max(0, d.monthlyPayment - interest);
            const share =
              d.monthlyPayment > 0 ? Math.round((interest / d.monthlyPayment) * 100) : 0;
            return (
              <div className="dsp" key={d.uuid}>
                <div className="dsphead">
                  <b style={{ fontSize: 15 }}>{name(d)}</b>
                  <span className="mut">
                    {index.get(d.uuid)?.rate !== undefined
                      ? `${rate(index.get(d.uuid)?.rate)} on ${money(d.balance)}`
                      : money(d.balance)}
                  </span>
                  <span className="amt tnum">
                    {money(d.monthlyPayment)}
                    <span className="mut" style={{ fontWeight: 400 }}>
                      {" "}
                      a month
                    </span>
                  </span>
                </div>
                <div className="dspbar">
                  <span style={{ flex: interest, background: "var(--warn)" }} />
                  <span style={{ flex: prin, background: "var(--debt)" }} />
                </div>
                <div className="dspfoot">
                  <span>
                    <i className="lgd" style={{ background: "var(--warn)" }} />
                    {money(interest)} interest
                  </span>
                  <span>
                    <i className="lgd" style={{ background: "var(--debt)" }} />
                    {money(prin)} off the balance
                  </span>
                  <span
                    className="pc"
                    style={{ color: share > 40 ? "var(--warn)" : "var(--ink2)" }}
                  >
                    {share}% of this payment is interest
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="card pad" style={{ marginBottom: 16 }}>
        <h2 style={{ marginBottom: 4 }}>
          When each one clears
          <ChartInfo k="debtsched" inline />
        </h2>
        <div className="legend" style={{ marginBottom: 18 }}>
          {naive && (
            <span>
              <i className="lgd" style={{ background: "var(--before)" }} />
              Blue is today&rsquo;s path
            </span>
          )}
          <span>
            <i className="lgd" style={{ background: "var(--after)" }} />
            Gold is Abi&rsquo;s plan
          </span>
        </div>
        <div id="debtsched" data-testid="debt-sched">
          <div className="sched">
            {clears.map(({ d, now, plan }) => {
              const never = !!now?.never;
              const nNever = "With today’s plan you would never be able to pay off this debt";
              const m =
                now?.at !== undefined && plan.at !== undefined
                  ? Math.round((now.at - plan.at) * 12)
                  : undefined;
              const gain = never
                ? plan.at !== undefined
                  ? `Paid off by ${yearLabel(plan.at)}`
                  : ""
                : m === undefined
                  ? ""
                  : m === 0
                    ? "The same"
                    : `${Math.abs(m)} months ${m > 0 ? "sooner" : "later"}`;
              return (
                <div key={d.uuid} style={{ display: "contents" }}>
                  <div className="nm">{name(d)}</div>
                  <div className="schbars">
                    {naive && (
                      <div className="schrow">
                        <span className="schlab">now</span>
                        <span className="schtrack">
                          <span
                            className={`schfill${never ? " never" : ""}`}
                            style={{
                              width: `${never || now?.at === undefined ? 100 : X(now.at)}%`,
                              background: "var(--before)",
                            }}
                            title={never ? nNever : undefined}
                          >
                            {!never && now?.at !== undefined ? Math.floor(now.at) : ""}
                          </span>
                          {never && (
                            <span
                              className="schwarn"
                              title={nNever}
                              aria-label={nNever}
                              role="img"
                              data-testid="debt-never"
                            >
                              &#9888;
                            </span>
                          )}
                        </span>
                      </div>
                    )}
                    <div className="schrow">
                      <span className="schlab">plan</span>
                      <span className="schtrack">
                        <span
                          className="schfill"
                          style={{
                            width: `${plan.at !== undefined ? X(plan.at) : 100}%`,
                            background: "var(--after)",
                          }}
                        >
                          {plan.at !== undefined ? Math.floor(plan.at) : "later"}
                        </span>
                      </span>
                    </div>
                  </div>
                  <div className={`schgain${never || (m ?? 0) < 0 ? " never" : ""}`}>{gain}</div>
                </div>
              );
            })}
            <div className="schaxis">
              <span>{lo}</span>
              <span>{Math.round((lo + hi) / 2)}</span>
              <span>{hi}</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export function DebtPage() {
  return (
    <PlanPage title="pages.debt.title" sub="pages.debt.sub" testId="debt">
      {(ctx) => <Body ctx={ctx} />}
    </PlanPage>
  );
}
